import React, { useState } from 'react';
import { DashboardLayout } from '@/components/shared/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  FileText, 
  GitBranch, 
  BarChart3, 
  Shield, 
  Scale, 
  Brain, 
  FileSearchIcon, 
  Settings,
  Activity,
  AlertCircle,
  CheckCircle2,
  Clock,
  TrendingUp,
  Server,
  Database,
  Lock
} from 'lucide-react';

// Import admin tabs
import { UserManagementTab } from './tabs/UserManagementTab';
import ContentTab from './tabs/ContentTab';
import CrossDisciplinaryTab from './tabs/CrossDisciplinaryTab';
import AnalyticsTab from './tabs/AnalyticsTab';
import ComplianceBasicTab from './tabs/ComplianceBasicTab';
import ComplianceFullTab from './tabs/ComplianceFullTab';
import AILearningTab from './tabs/AILearningTab';
import LogsTab from './tabs/LogsTab';
import ConfigTab from './tabs/ConfigTab';

type AdminTabType = 'dashboard' | 'users' | 'content' | 'cross-disciplinary' | 'analytics' | 'compliance-basic' | 'compliance-full' | 'ai-learning' | 'logs' | 'config';

export function AdminDashboard() {
  const [activeTab, setActiveTab] = useState<AdminTabType>('dashboard');

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Activity, description: 'System Overview & Analytics' },
    { id: 'users', label: 'Users', icon: Users, description: 'User Management & Roles' },
    { id: 'content', label: 'Content', icon: FileText, description: 'Content Management & Review' },
    { id: 'cross-disciplinary', label: 'Cross-Disciplinary', icon: GitBranch, description: 'Knowledge Connections' },
    { id: 'analytics', label: 'Analytics', icon: BarChart3, description: 'Platform Analytics & Reports' },
    { id: 'compliance-basic', label: 'Security', icon: Shield, description: 'Security & Compliance' },
    { id: 'compliance-full', label: 'Legal Automation', icon: Scale, description: 'Legal & Policy Automation' },
    { id: 'ai-learning', label: 'AI & Learning', icon: Brain, description: 'AI Systems & Learning' },
    { id: 'logs', label: 'System Logs', icon: FileSearchIcon, description: 'System Logs & Monitoring' },
    { id: 'config', label: 'Configuration', icon: Settings, description: 'System Configuration' }
  ];

  const systemStats = [
    { label: 'Active Users', value: '1,247', icon: Users, color: 'bg-blue-50', trend: '+12%' },
    { label: 'Content Items', value: '8,392', icon: FileText, color: 'bg-green-50', trend: '+5%' },
    { label: 'System Health', value: '99.9%', icon: Server, color: 'bg-purple-50', trend: '+0.1%' },
    { label: 'Storage Used', value: '67%', icon: Database, color: 'bg-orange-50', trend: '+3%' }
  ];

  const recentActivity = [
    { title: 'New user registration spike', type: 'info', time: '2 hours ago', icon: Users },
    { title: 'Content review completed', type: 'success', time: '4 hours ago', icon: CheckCircle2 },
    { title: 'Security scan completed', type: 'success', time: '6 hours ago', icon: Lock },
    { title: 'System backup finished', type: 'info', time: '8 hours ago', icon: Database }
  ];

  const alerts = [
    { title: 'High CPU usage detected', severity: 'warning', time: '1 hour ago' },
    { title: 'Storage approaching limit', severity: 'error', time: '3 hours ago' },
    { title: 'Scheduled maintenance reminder', severity: 'info', time: '1 day ago' }
  ];

  const renderMainContent = () => {
    switch (activeTab) {
      case 'users':
        return <UserManagementTab />;
      case 'content':
        return <ContentTab />;
      case 'cross-disciplinary':
        return <CrossDisciplinaryTab />;
      case 'analytics':
        return <AnalyticsTab />;
      case 'compliance-basic':
        return <ComplianceBasicTab />;
      case 'compliance-full':
        return <ComplianceFullTab />;
      case 'ai-learning':
        return <AILearningTab />;
      case 'logs':
        return <LogsTab />;
      case 'config':
        return <ConfigTab />;
      default:
        return (
          <div className="space-y-6">
            {/* Welcome Section */}
            <Card className="bg-white border border-gray-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h1 className="text-2xl font-bold text-black mb-2">Administration Dashboard</h1>
                    <p className="text-gray-600">Comprehensive platform management and analytics</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">System Status</p>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                      <span className="text-sm font-medium text-black">Operational</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* System Stats Grid */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {systemStats.map((stat, index) => (
                <Card key={index} className="bg-white border border-gray-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${stat.color}`}>
                        <stat.icon className="h-5 w-5 text-gray-700" />
                      </div>
                      <div className="flex-1">
                        <p className="text-2xl font-bold text-black">{stat.value}</p>
                        <p className="text-sm text-gray-600">{stat.label}</p>
                        <div className="flex items-center gap-1 mt-1">
                          <TrendingUp className="h-3 w-3 text-green-500" />
                          <span className="text-xs text-green-600">{stat.trend}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Main Dashboard Content */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Activity */}
              <Card className="bg-white border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-black">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {recentActivity.map((item, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <div className={`p-2 rounded-lg ${
                        item.type === 'success' ? 'bg-green-100' : 
                        item.type === 'error' ? 'bg-red-100' : 'bg-blue-100'
                      }`}>
                        <item.icon className={`h-4 w-4 ${
                          item.type === 'success' ? 'text-green-600' : 
                          item.type === 'error' ? 'text-red-600' : 'text-blue-600'
                        }`} />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-black">{item.title}</p>
                        <p className="text-sm text-gray-500">{item.time}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* System Alerts */}
              <Card className="bg-white border border-gray-200">
                <CardHeader>
                  <CardTitle className="text-black">System Alerts</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {alerts.map((alert, index) => (
                    <div key={index} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <AlertCircle className={`h-5 w-5 ${
                        alert.severity === 'error' ? 'text-red-500' :
                        alert.severity === 'warning' ? 'text-yellow-500' : 'text-blue-500'
                      }`} />
                      <div className="flex-1">
                        <p className="font-medium text-black">{alert.title}</p>
                        <p className="text-sm text-gray-500">{alert.time}</p>
                      </div>
                      <Badge variant={
                        alert.severity === 'error' ? 'destructive' :
                        alert.severity === 'warning' ? 'secondary' : 'default'
                      }>
                        {alert.severity}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card className="bg-white border border-gray-200">
              <CardHeader>
                <CardTitle className="text-black">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button variant="outline" className="h-auto flex-col gap-2 p-4">
                    <Users className="h-6 w-6" />
                    <span>Manage Users</span>
                  </Button>
                  <Button variant="outline" className="h-auto flex-col gap-2 p-4">
                    <FileText className="h-6 w-6" />
                    <span>Review Content</span>
                  </Button>
                  <Button variant="outline" className="h-auto flex-col gap-2 p-4">
                    <BarChart3 className="h-6 w-6" />
                    <span>View Analytics</span>
                  </Button>
                  <Button variant="outline" className="h-auto flex-col gap-2 p-4">
                    <Settings className="h-6 w-6" />
                    <span>System Config</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        );
    }
  };

  const sidebarBottom = (
    <button className="w-full flex items-center gap-3 px-3 py-3 rounded-lg text-gray-600 hover:bg-gray-200 hover:text-black transition-all duration-200">
      <Activity className="h-5 w-5 flex-shrink-0" />
      <span className="overflow-hidden transition-all duration-300 whitespace-nowrap">
        System Monitor
      </span>
    </button>
  );

  return (
    <DashboardLayout
      title="Admin Portal"
      menuItems={menuItems}
      activeTab={activeTab}
      onTabChange={(tabId: string) => setActiveTab(tabId as AdminTabType)}
      sidebarBottom={sidebarBottom}
    >
      {renderMainContent()}
    </DashboardLayout>
  );
} 